import {AsyncInlineError, inlineError} from '@s-ui/decorators'

import {PhoneSellerValueObject} from '../../seller/ValueObjects/PhoneSellerValueObject.js'
import {GetAdContactPhoneAdsError} from '../Errors/GetAdContactPhoneAdsError.js'
import AdsRepository from './AdsRepository.js'

const DEFAULT_ORIGIN = 'API Web - Detail'

/**
 * @class
 * @implements AdsRepository
 */
export default class HTTPAdsRepository extends AdsRepository {
  /**
   * @constructor
   * @param {Object} deps
   * Config
   * @param {Config} deps.config
   * Fetchers
   * @param {Fetcher} deps.fetcher
   * @param {Fetcher} deps.fetchOrFail
   * Utils
   * @param {Object} deps.toQueryString
   * Logs
   * @param {Function} deps.log
   * Mappers
   * @param {Function} deps.adsSearchParamsHttpToValueObjectMapperFactory
   * @param {Function} deps.adsSearchParamsValueObjectToHttpMapperFactory
   * @param {Function} deps.fromRecommendedAdsRawDataToRecommendedAdsListVOMapper
   * @param {Function} deps.fromStatsResponseToVOMapper
   * @param {Function} deps.httpAdDetailsResponseToAdDetailsAggregateMapper
   * @param {Function} deps.httpMakeModelAdsListingToAdsListValueObjectMapper
   * @param {Function} deps.hTTPRelatedAdsResponseJSONtoAdEntityMapper
   * @param {Function} deps.httpRelatedSubscriptionAdsResponseToSubscriptionAdsListValueObjectMapper
   * @param {Function} deps.httpSearchResultsResponseToAdsListValueObjectMapper
   * @param {Function} deps.httpSearchUrlResponseToSearchDataAdsValueObjectMapper
   * Value Objects
   * @param {Function} deps.adsSearchParamsValueObjectFactory
   * Entities
   * @param {Function} deps.adEntityFactory
   * Errors
   * @param {Function} deps.getAdDetailsGenericErrorFactory
   * @param {Function} deps.getAdDetailsNotFoundErrorFactory
   * @param {Function} deps.getAdDetailsPartialContentErrorFactory
   * @param {Function} deps.getFinancingDataAdsError
   * @param {Function} deps.getRecommendedAdsGenericErrorFactory
   * @param {Function} deps.getSearchDataFromUrlError
   * @param {Function} deps.getSearchRequestObjectFromUrlError
   * @param {Function} deps.getSearchStatsGenericErrorFactory
   * @param {Function} deps.relatedAdsError
   * @param {Function} deps.reportAdError
   */
  constructor({
    // Config
    config,
    // Fecthers
    fetcher,
    fetchOrFail,
    // Utils
    toQueryString,
    // Logs
    log,
    // Mappers
    adsSearchParamsHttpToValueObjectMapperFactory,
    adsSearchParamsValueObjectToHttpMapperFactory,
    financingTermsRawDataToFinancingConditionEntityMapper,
    fromAdRawDataToAdEntityMapper,
    fromRecommendedAdsRawDataToRecommendedAdsListVOMapper,
    fromStatsResponseToVOMapper,
    httpAdDetailsResponseToAdDetailsAggregateMapper,
    httpMakeModelAdsListingToAdsListValueObjectMapper,
    hTTPRelatedAdsResponseJSONtoAdEntityMapper,
    httpRelatedSubscriptionAdsResponseToSubscriptionAdsListValueObjectMapper,
    httpSearchResultsResponseToAdsListValueObjectMapper,
    httpSearchUrlResponseToSearchDataAdsValueObjectMapper,
    httpSearchUrlResponseToSearchRequestObjectAdsValueObjectMapper,
    httpSubscriptionCommitmentOptionsResponseToSubscriptionCommitmentOptionsValueObjectMapper,
    httpSubscriptionSearchResultsResponseToSubscriptionAdsListValueObjectMapper,
    searchRequestJSONToSearchParamsRequestJSONMapper,
    // Value Objects
    adsListValueObject,
    adsSearchParamsValueObjectFactory,
    financingConditionEntityFactory,
    subscriptionAdsListValueObjectFactory,
    // Entities
    adEntityFactory,
    // Errors
    getAdDetailsGenericErrorFactory,
    getAdDetailsNotFoundErrorFactory,
    getAdDetailsPartialContentErrorFactory,
    getFinancingDataAdsError,
    getRecommendedAdsGenericErrorFactory,
    getSearchDataFromUrlError,
    getSearchRequestObjectFromUrlError,
    getSearchResultsErrorFactory,
    getSearchStatsGenericErrorFactory,
    getSubscriptionCommitmentOptionsAdsErrorFactory,
    getSubscriptionSearchResultsAdsErrorFactory,
    relatedAdsError,
    reportAdError
  }) {
    super()

    // Config
    this._config = config
    // Fecthers
    this._fetcher = fetcher
    this._fetchOrFail = fetchOrFail
    // Utils
    this._toQueryString = toQueryString
    // Logs
    this._log = log
    // Mappers
    this._adsSearchParamsHttpToValueObjectMapperFactory = adsSearchParamsHttpToValueObjectMapperFactory
    this._adsSearchParamsValueObjectToHttpMapperFactory = adsSearchParamsValueObjectToHttpMapperFactory
    this._financingTermsRawDataToFinancingConditionEntityMapper = financingTermsRawDataToFinancingConditionEntityMapper
    this._fromAdRawDataToAdEntityMapper = fromAdRawDataToAdEntityMapper
    this._fromRecommendedAdsRawDataToRecommendedAdsListVOMapper = fromRecommendedAdsRawDataToRecommendedAdsListVOMapper
    this._fromStatsResponseToVOMapper = fromStatsResponseToVOMapper
    this._httpAdDetailsResponseToAdDetailsAggregateMapper = httpAdDetailsResponseToAdDetailsAggregateMapper
    this._httpMakeModelAdsListingToAdsListValueObjectMapper = httpMakeModelAdsListingToAdsListValueObjectMapper
    this._hTTPRelatedAdsResponseJSONtoAdEntityMapper = hTTPRelatedAdsResponseJSONtoAdEntityMapper
    this._httpRelatedSubscriptionAdsResponseToSubscriptionAdsListValueObjectMapper =
      httpRelatedSubscriptionAdsResponseToSubscriptionAdsListValueObjectMapper
    this._httpSearchResultsResponseToAdsListValueObjectMapper = httpSearchResultsResponseToAdsListValueObjectMapper
    this._httpSearchUrlResponseToSearchDataAdsValueObjectMapper = httpSearchUrlResponseToSearchDataAdsValueObjectMapper
    this._httpSearchUrlResponseToSearchRequestObjectAdsValueObjectMapper =
      httpSearchUrlResponseToSearchRequestObjectAdsValueObjectMapper
    this._httpSubscriptionCommitmentOptionsResponseToSubscriptionCommitmentOptionsValueObjectMapper =
      httpSubscriptionCommitmentOptionsResponseToSubscriptionCommitmentOptionsValueObjectMapper
    this._httpSubscriptionSearchResultsResponseToSubscriptionAdsListValueObjectMapper =
      httpSubscriptionSearchResultsResponseToSubscriptionAdsListValueObjectMapper
    this._searchRequestJSONToSearchParamsRequestJSONMapper = searchRequestJSONToSearchParamsRequestJSONMapper
    // Entities
    this._adEntityFactory = adEntityFactory
    // Value Objects
    this._adsListValueObject = adsListValueObject
    this._adsSearchParamsValueObjectFactory = adsSearchParamsValueObjectFactory
    this._financingConditionEntityFactory = financingConditionEntityFactory
    this._subscriptionAdsListValueObjectFactory = subscriptionAdsListValueObjectFactory
    // Errors
    this._getAdDetailsGenericErrorFactory = getAdDetailsGenericErrorFactory
    this._getAdDetailsNotFoundErrorFactory = getAdDetailsNotFoundErrorFactory
    this._getAdDetailsPartialContentErrorFactory = getAdDetailsPartialContentErrorFactory
    this._getFinancingDataAdsError = getFinancingDataAdsError
    this._getRecommendedAdsGenericErrorFactory = getRecommendedAdsGenericErrorFactory
    this._getSearchDataFromUrlError = getSearchDataFromUrlError
    this._getSearchRequestObjectFromUrlError = getSearchRequestObjectFromUrlError
    this._getSearchResultsErrorFactory = getSearchResultsErrorFactory
    this._getSearchStatsGenericErrorFactory = getSearchStatsGenericErrorFactory
    this._getSubscriptionCommitmentOptionsAdsErrorFactory = getSubscriptionCommitmentOptionsAdsErrorFactory
    this._getSubscriptionSearchResultsAdsErrorFactory = getSubscriptionSearchResultsAdsErrorFactory
    this._relatedAdsError = relatedAdsError
    this._reportAdError = reportAdError
  }

  _getCustomHeaders({variant}) {
    if (variant) return {'X-Adevinta-MT-Variant': variant}
  }

  @inlineError
  async sendComplaint(requestVO) {
    const adId = requestVO.getAdId()

    const body = requestVO.getBody()

    const url = `${this._config.get('API_URL')}/complaint/${adId}`

    const [error] = await this._fetchOrFail.post(url, body)

    return error ? Promise.reject(error) : Promise.resolve()
  }

  /**
   * @param {Object} params
   * @param {Array} params.bodyTypes
   * @param {Number} params.fuelType
   * @param {Number} params.kmsMax
   * @param {Number} params.kmsMin
   * @param {String} params.modelLiteral
   * @param {Number} params.model
   * @param {Number} params.brand
   * @param {Number} params.priceMin
   * @param {Number} params.priceMax
   * @param {Number} params.province
   * @param {Number} params.yearMax
   * @param {Number} params.yearMin
   * @param {String} params.keywords
   * @param {Number} params.count
   * @param {Array} params.equipments
   * @param {String} params.version
   * @param {Number} params.transmissionTypeId
   * @param {Number} params.sellerTypeId
   * @param {Number} params.offerType
   * @param {Number} params.minPowerHp
   * @param {Number} params.maxPowerHp
   * @param {Boolean} params.withWarranty
   * @param {Boolean} params.financed
   * @param {Number} params.section1Id
   * @param {Number} params.section2Id
   * @param {Number} params.section3Id
   * @param {Number} params.contractId
   * @param {String} params.sortField
   * @param {String} params.sortType
   * @param {Number} params.offset
   * @return {Array<id: number, url: string, img: string, price: number,
   * description: string>}
   */
  getHomeAds({
    bodyTypes,
    fuelType,
    kmsMax,
    kmsMin,
    modelLiteral,
    model,
    brand,
    priceMin,
    priceMax,
    province,
    yearMax,
    yearMin,
    keywords,
    count,
    equipments,
    version,
    transmissionTypeId,
    sellerTypeId,
    offerType,
    minPowerHp,
    maxPowerHp,
    withWarranty,
    financed,
    section1Id,
    section2Id,
    section3Id,
    contractId,
    sortField,
    sortType,
    offset
  } = {}) {
    const url = `${this._config.get('API_URL')}/ads/home`
    const params = this._adsSearchParamsValueObjectToHttpMapperFactory({
      bodyTypes,
      fuelType,
      kmsMax,
      kmsMin,
      modelLiteral,
      model,
      brand,
      priceMin,
      priceMax,
      province,
      yearMax,
      yearMin,
      keywords,
      count,
      equipments,
      version,
      transmissionTypeId,
      sellerTypeId,
      offerType,
      minPowerHp,
      maxPowerHp,
      withWarranty,
      financed,
      section1Id,
      section2Id,
      section3Id,
      contractId,
      sortField,
      sortType,
      offset
    }).map()

    this._log('Searching home ads by params:', params)

    return this._fetcher.get(url, {params}).then(({data}) =>
      this._adsListValueObject({
        items: data.map(rawAd => this._adEntityFactory({...rawAd, config: this._config}))
      })
    )
  }

  /**
   * @param {RelatedAdsRequestValueObject} requestVO
   * @return {Promise<AdsListValueObjectLegacy, Error>}
   */
  @inlineError
  async getRelatedAds(requestVO) {
    try {
      const {adId, count, preferredContractModes} = requestVO.toJSON()
      this._log(`Getting related ads from ad id: ${adId}`)

      const {data} = await this._fetcher.get(`${this._config.get('API_URL')}/vehicles/${adId}/similar/`, {
        params: {
          count,
          ...(preferredContractModes && {preferredContractModes})
        },
        paramsSerializer: params => this._toQueryString(params, {arrayFormat: 'repeat'})
      })

      const {items} = data
      return this._adsListValueObject({
        items: items.map(adData => this._hTTPRelatedAdsResponseJSONtoAdEntityMapper.map(adData))
      })
    } catch (error) {
      throw this._relatedAdsError('[HTTPAdsRepository#getRelatedAds] request failed')
    }
  }

  /**
   * @param {RelatedAdsRequestValueObject} requestVO
   * @return {Promise<AdsListValueObjectLegacy, Error>}
   */
  @inlineError
  async getRelatedRentingAds(requestVO) {
    try {
      const {adId, count} = requestVO.toJSON()
      this._log(`Getting related ads from ad id: ${adId}`)

      const {data} = await this._fetcher.get(`${this._config.get('API_URL')}/similar-ads/${adId}?size=${count}`)

      const {items} = data

      return this._adsListValueObject({
        items: items.map(adData => this._fromAdRawDataToAdEntityMapper.map(adData))
      })
    } catch (error) {
      throw this._relatedAdsError('[HTTPAdsRepository#getRelatedRentingAds] request failed')
    }
  }

  /**
   * @param {RelatedAdsRequestValueObject} requestVO
   * @return {Promise<AdsListValueObjectLegacy, Error>}
   */
  @inlineError
  async getRelatedSubscriptionAds(requestVO) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES

    const {modelId, count, subscriptionContext} = requestVO.toJSON()
    this._log(`Getting related subscription ads to model id: ${modelId}`)

    const {SUBSCRIPTION_REFERRALS} = this._config.get('subscription')

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._relatedAdsError('[HTTPAdsRepository#getRelatedSubscriptionAds] request failed')
        },
        [NOT_FOUND]: () => {
          return this._relatedAdsError('[HTTPAdsRepository#getRelatedSubscriptionAds] subscription ads not found')
        }
      })
      .get(`${this._config.get('API_URL')}/subscriptions/api/externals/fleet-catalog`, {
        params: {
          region: 'ES',
          source: 'cochesnet',
          referral_content: SUBSCRIPTION_REFERRALS[subscriptionContext]
        }
      })

    if (error) return Promise.reject(error)

    const {data} = response
    return this._httpRelatedSubscriptionAdsResponseToSubscriptionAdsListValueObjectMapper
      .setParams({count, modelId})
      .map(data)
  }

  /**
   * @param {ValueObject} requestValueObject
   * @return {Promise}
   */
  async reportAd(requestValueObject) {
    const {adId, queryTypeId, description, captchaToken} = requestValueObject.toJSON()

    try {
      await this._fetcher.post(`${this._config.get('API_URL')}/claims`, {
        adId,
        queryTypeId,
        description,
        captchaToken
      })

      return true
    } catch (error) {
      throw this._reportAdError('[HTTPAdsRepository#reportAd] request failed')
    }
  }

  /**
   * @param {Object} params
   * @param {String} params.url
   * @return {Promise}
   */
  @inlineError
  async getSearchDataFromUrl({urlVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const url = urlVO.getUrl()

    // Temporary fix to avoid the error pagination error
    // Experiment abtest1_web_mobile_coches_filter_multi_make_model_version
    const hasNewVehicleQueryParams = url.includes('MakeIds')

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getSearchDataFromUrlError('Url can not be resolved')
        }
      })
      .get(`${this._config.get('API_URL')}/semantic${url}`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._httpSearchUrlResponseToSearchDataAdsValueObjectMapper.map(
      data,
      // Temporary fix to avoid the error pagination error
      // Experiment abtest1_web_mobile_coches_filter_multi_make_model_version
      {hasNewVehicleQueryParams}
    )
  }

  /**
   * @param {Object} params
   * @param {Object} params.searchRequestObjectValueObject
   * @return {Promise}
   */
  @inlineError
  async getSearchResults({listingSectionId, searchRequestObjectValueObject, withAggregations, variant}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const headers = this._getCustomHeaders({variant})
    const searchRequestObject = searchRequestObjectValueObject.toJSON()
    const searchParamsRequest = this._searchRequestJSONToSearchParamsRequestJSONMapper
      .setParams({...searchRequestObject, listingSectionId})
      .map()

    const baseUrl = `${this._config.get('API_URL')}/search`
    const url = withAggregations ? `${baseUrl}/listing` : baseUrl

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getSearchResultsErrorFactory('[HTTPAdsRepository#getSearchResults] request failed')
        }
      })
      .post(url, {...searchParamsRequest}, {headers})

    if (error) return Promise.reject(error)

    const {data} = response

    return this._httpSearchResultsResponseToAdsListValueObjectMapper.map(data)
  }

  /**
   * @param {Object} params
   * @param {Object} params.searchRequestObjectValueObject
   * @return {Promise}
   */
  @inlineError
  async getModelDetailRelevantAds({searchRequestObjectValueObject}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const searchRequestObject = searchRequestObjectValueObject.toJSON()
    const searchParamsRequest = this._searchRequestJSONToSearchParamsRequestJSONMapper
      .setParams(searchRequestObject)
      .map()

    const url = `${this._config.get('API_URL')}/vehicle/modeldetail/ads/listing`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getRecommendedAdsGenericErrorFactory(
            '[HTTPAdsRepository#getModelDetailRelevantAds] request failed'
          )
        }
      })
      .post(url, {...searchParamsRequest})

    if (error) return Promise.reject(error)

    const {data} = response

    return this._httpMakeModelAdsListingToAdsListValueObjectMapper.map(data)
  }

  /**
   * @param {Object} params
   * @param {ValueObject} params.subscriptionSearchRequestVO
   * @return {Promise}
   */
  @inlineError
  async getSubscriptionSearchResults({subscriptionSearchRequestVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const URL_PROTOCOL = this._config.get('URL_PROTOCOL')
    const API_GW_SUBDOMAIN_SUBSCRIPTION = this._config.get('API_GW_SUBDOMAIN_SUBSCRIPTION')
    const API_GW_DOMAIN = this._config.get('API_GW_DOMAIN')

    const apiUrl = `${URL_PROTOCOL}${API_GW_SUBDOMAIN_SUBSCRIPTION}${API_GW_DOMAIN}`

    const subscriptionSearchRequest = subscriptionSearchRequestVO.toJSON()

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getSubscriptionSearchResultsAdsErrorFactory(
            '[HTTPAdsRepository#getSubscriptionSearchResultsAdsError] request failed'
          )
        }
      })
      .post(`${apiUrl}/search`, {
        ...subscriptionSearchRequest
      })

    if (error) return Promise.reject(error)

    const {data} = response

    return this._httpSubscriptionSearchResultsResponseToSubscriptionAdsListValueObjectMapper.map(data)
  }

  @inlineError
  async getSubscriptionCommitmentOptions() {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const apiUrl = this._config.get('API_URL')

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getSubscriptionCommitmentOptionsAdsErrorFactory(
            '[HTTPAdsRepository#getSubscriptionCommitmentOptionsAdsError] request failed'
          )
        }
      })
      .get(`${apiUrl}/renting/commitments`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._httpSubscriptionCommitmentOptionsResponseToSubscriptionCommitmentOptionsValueObjectMapper.map(data)
  }

  /**
   * @method
   * @return {Object}
   */
  _getUserIdCustomHeaders({userId}) {
    if (userId) return {'X-Adevinta-MT-UserId': userId}
  }

  /**
   * @param {Object} params
   * @param {String|Number} params.adId
   * @return {Promise}
   */
  @inlineError
  async getAdDetails({adId, isPreview = false, userId, expand}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NOT_FOUND, PARTIAL_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {EXPAND_TYPES} = this._config.get('COMMON').QUERY_PARAMS

    const apiUrl = this._config.get('API_URL')

    const detailBaseUrl = `${apiUrl}/details/${adId}`
    const expandParam = expand ? `?expand=${EXPAND_TYPES[expand]}` : ''
    const previewPath = '/preview'

    const endpoint = isPreview ? `${detailBaseUrl}${previewPath}` : `${detailBaseUrl}${expandParam}`

    const customHeaders = (isPreview && this._getUserIdCustomHeaders({userId})) || {}

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getAdDetailsGenericErrorFactory({
            message: '[HTTPAdsRepository#getAdDetailsGenericError]',
            status: GENERIC,
            origin: DEFAULT_ORIGIN
          })
        },
        [NOT_FOUND]: () => {
          return this._getAdDetailsNotFoundErrorFactory({
            message: '[HTTPAdsRepository#getAdDetailsNotFoundError]',
            status: NOT_FOUND,
            origin: DEFAULT_ORIGIN
          })
        }
      })
      .get(endpoint, {headers: customHeaders})

    if (error) return Promise.reject(error)

    const {data, status} = response

    if (isPreview && status === PARTIAL_CONTENT) {
      return Promise.reject(
        this._getAdDetailsPartialContentErrorFactory({
          adId: data.adId,
          message: '[HTTPAdsRepository#getAdDetailsPartialContentError]',
          status: PARTIAL_CONTENT,
          origin: DEFAULT_ORIGIN
        })
      )
    }
    return this._httpAdDetailsResponseToAdDetailsAggregateMapper.map(data)
  }

  @inlineError
  async getFinancingData({adId, entry, terms}) {
    const queryString = this._toQueryString({entry, terms})

    const baseUrl = `${this._config.get('API_URL')}/v1/vehicles/${adId}/terms`
    const termsUrl = queryString !== '' ? `${baseUrl}?${queryString}` : baseUrl

    const {GENERIC} = this._config.get('COMMON').ERRORS
    const [error, financingData] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e => {
          return this._getFinancingDataAdsError(e.message)
        }
      })
      .get(termsUrl)

    if (error) return Promise.reject(error)

    return this._financingTermsRawDataToFinancingConditionEntityMapper.map({
      ...financingData.data,
      termsUrl
    })
  }

  @inlineError
  async getSearchStats({searchStatsRequestVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const url = `${this._config.get('API_URL')}/search`
    const [requestError, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getSearchStatsGenericErrorFactory(`Could not get search stats`)
        }
      })
      .post(url, {
        ...searchStatsRequestVO.toJSON()
      })

    if (requestError) return Promise.reject(requestError)

    return this._fromStatsResponseToVOMapper.map(response.data)
  }

  @AsyncInlineError()
  async getRecommendedAds({adId, maxItems}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getRecommendedAdsGenericErrorFactory(`Could not get recommended ads`)
        }
      })
      .get(`${this._config.get('API_URL')}/similar-ads/${adId}?size=${maxItems}`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromRecommendedAdsRawDataToRecommendedAdsListVOMapper.map(data)
  }

  @AsyncInlineError()
  async getAdContactPhone({adId, isPrivate = false}) {
    const {NO_CONTENT, NOT_FOUND, TOO_MANY_REQUESTS, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES

    const endpoint = isPrivate
      ? `${this._config.get('API_URL')}/ads/${adId}/detail/phone`
      : `${this._config.get('API_URL')}/details/${adId}/phone`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: () => GetAdContactPhoneAdsError.createAdNotFoundError(),
        [NO_CONTENT]: () => GetAdContactPhoneAdsError.createUserPhoneNotAvailableError(),
        [UNAUTHORIZED]: () => GetAdContactPhoneAdsError.createUnauthorizedError(),
        [TOO_MANY_REQUESTS]: () => GetAdContactPhoneAdsError.createTooManyRequestsError()
      })
      .get(endpoint, {withCredentials: true})

    if (error) return Promise.reject(error)

    if (response.status === NO_CONTENT) {
      return Promise.reject(GetAdContactPhoneAdsError.createUserPhoneNotAvailableError())
    }

    return PhoneSellerValueObject.create({phone: response.data.phone})
  }
}
